import { useEffect, useMemo, useState } from 'react';
import CheckableSelect from 'components/CheckableSelect/CheckableSelect';
import TimeRange from 'components/TimeRange/TimeRange';
import _ from 'lodash';
import { errorNotification } from 'utils/notifications';
import { EWeekdays } from '@timeedit/types/lib/enums';
import type { TWeekdaySelection } from '@timeedit/types/lib/types';
// Types
import { Button, Icon } from '@timeedit/ui-components';
// Components
import { Modal } from '@timeedit/ui-components/antd';
// Styles
import './TimeSlotInput.scss';

interface TimeSlotInputProps {
  timeSlotIndex?: number;
  timeSlots?: TWeekdaySelection[];
  onChange: (timeSlot: TWeekdaySelection, timeSlotIndex?: number) => void;
  open: boolean;
  onClose: () => void;
}

const emptyTimeSlot: TWeekdaySelection = {
  weekdays: [],
  times: [],
};

const TimeSlotInput = ({
  timeSlotIndex,
  timeSlots,
  onChange,
  open,
  onClose,
}: TimeSlotInputProps) => {
  const [localTimeSlot, setLocalTimeSlot] =
    useState<TWeekdaySelection>(emptyTimeSlot);

  useEffect(() => {
    setLocalTimeSlot(timeSlots?.[timeSlotIndex] || emptyTimeSlot);
  }, [timeSlots, timeSlotIndex]);

  const onOk = () => {
    if (localTimeSlot.weekdays.length === 0) {
      return errorNotification('Please select at least one weekday');
    }
    if (localTimeSlot.times.length === 0) {
      return errorNotification('Please add at least one time slot');
    }
    onChange(localTimeSlot, timeSlotIndex);
    handleClose();
  };

  const handleClose = () => {
    setLocalTimeSlot(emptyTimeSlot);
    onClose();
  };

  const weekdayOptions = useMemo(() => {
    const otherTimeslots = timeSlots?.slice() ?? [];
    if (timeSlotIndex != null) otherTimeslots.splice(timeSlotIndex, 1);
    const otherSelectedDays = otherTimeslots.flatMap((slot) => slot.weekdays);

    return Object.keys(EWeekdays).map((key) => ({
      value: key,
      label: _.capitalize(key),
      disabled: otherSelectedDays.includes(key),
    }));
  }, [timeSlots, timeSlotIndex]);

  return (
    <Modal title="Time Slot" open={open} onCancel={handleClose} onOk={onOk}>
      <div className="time-slot-input">
        <CheckableSelect
          label="Select weekday(s)"
          tooltip="Select weekday(s)"
          options={weekdayOptions}
          value={localTimeSlot.weekdays}
          onChange={(value: string[]) => {
            setLocalTimeSlot({ ...localTimeSlot, weekdays: value });
          }}
          highlightValue={[EWeekdays.SUN]}
          data-testid="select-weekday"
        />
        <div className="add-btn" data-testid="time-slot-input-add-btn">
          <Button
            onClick={() => {
              setLocalTimeSlot({
                ...localTimeSlot,
                times: [
                  { clockTimeStart: 0, clockTimeEnd: 0 },
                  ...localTimeSlot.times,
                ],
              });
            }}
            size="small"
            icon={<Icon id="PlusOutlined" alt="Plus" />}
          >
            Add time slot
          </Button>
        </div>
        <div>
          {localTimeSlot.times.map((time, index) => (
            <div key={index} className="time-range">
              <TimeRange
                value={[time.clockTimeStart, time.clockTimeEnd]}
                onChange={(value) => {
                  const newTimes = localTimeSlot.times.map((t, i) =>
                    i === index
                      ? {
                          clockTimeStart: value?.[0] || 0,
                          clockTimeEnd: value?.[1] || 0,
                        }
                      : t
                  );
                  setLocalTimeSlot({ ...localTimeSlot, times: newTimes });
                }}
              />
              <Button
                onClick={() => {
                  const newTimes = localTimeSlot.times.filter(
                    (_, i) => i !== index
                  );
                  setLocalTimeSlot({ ...localTimeSlot, times: newTimes });
                }}
                type="text"
                size="small"
                icon={<Icon id="DeleteOutlined" alt="Delete" />}
              ></Button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default TimeSlotInput;
